/**
 * SPEC-UnifiedQADetail Phase 2 — extracted Photos panel.
 *
 * viewMode-branching internal per §"Component composition":
 *   - agent: POD tab + Verified-past tab (the v1 layout).
 *   - senior: a stub placeholder that yields to the senior layout owned by
 *     `qa-detail-v3.tsx` for the one-cycle delegation window. A follow-up
 *     SPEC will move the senior scroll-list + draw-region toolbox into this
 *     file alongside the agent layout.
 *
 * The agent variant accepts the slim payload the agent surface needs
 * (podPhotos array, verifiedPast list, verifiedPastCount) — anything that
 * would carry model overlays or AI confidence stays out of the prop shape.
 */

import { useState } from "react";
import type { ViewMode } from "@/hooks/use-view-mode";
import { Starling } from "@/lib/starling-brand";
import { useProbedImageSrc } from "@/lib/photo-retry";

export interface PhotosPanelPhoto {
  id?: string;
  url: string;
  order?: number | null;
}

export interface PhotosPanelVerifiedPast {
  id: string;
  completedAt: string | null;
  dropZone: string | null;
  // SPEC-AgentPhotos-VerifiedPast-Fix — the past-tab render now mirrors POD
  // (main image + caption + thumbnail strip), so the prop shape needs the
  // CloudFront-wrapped URL on the wire plus the actor that marked the past
  // delivery Good. Matches `AgentVerifiedPastEntry` in agent-queries.ts.
  photoUrl: string | null;
  verifiedBy: string | null;
}

export interface PhotosPanelProps {
  viewMode: ViewMode;
  podPhotos: PhotosPanelPhoto[];
  verifiedPast?: PhotosPanelVerifiedPast[];
  verifiedPastCount?: number | null;
}

const TOKENS = {
  bg: "#F7F8FA",
  card: "#FFFFFF",
  sub: "#F3F4F6",
  border: "#E5E7EB",
  text: "#111827",
  txt2: "#6B7280",
  txt3: "#9CA3AF",
  purple: Starling.purple,
  purpleSub: "rgba(89,47,150,.12)",
  goodCalm: "#166534",
  goodCalmBg: "#DCFCE7",
} as const;

export function PhotosPanel({
  viewMode,
  podPhotos,
  verifiedPast = [],
  verifiedPastCount,
}: PhotosPanelProps) {
  if (viewMode === "senior") {
    // Senior layout lives inside qa-detail-v3.tsx for the one-cycle window.
    // v4 dispatches senior view to v3 directly, so this branch is the
    // defense-in-depth no-op for any caller that mounts PhotosPanel under
    // a senior viewMode.
    return null;
  }
  return <AgentPhotosLayout podPhotos={podPhotos} verifiedPast={verifiedPast} verifiedPastCount={verifiedPastCount} />;
}

function AgentPhotosLayout({
  podPhotos,
  verifiedPast,
  verifiedPastCount,
}: {
  podPhotos: PhotosPanelPhoto[];
  verifiedPast: PhotosPanelVerifiedPast[];
  verifiedPastCount: number | null | undefined;
}) {
  const [activePhoto, setActivePhoto] = useState(0);
  // SPEC-AgentPhotos-VerifiedPast-Fix §"Render layout" — parallel `activePast`
  // state so the POD and Past tabs don't fight over a shared active index.
  // Defensive clamp to 0 if the array shrinks below activePast on next render
  // (e.g. the verified-past list is invalidated and refetched with fewer rows).
  const [activePast, setActivePast] = useState(0);
  const [tab, setTab] = useState<"pod" | "past">("pod");
  // A2 (2026-09-24 validation): these are CSS background images, which give
  // no load error, so the URL is probed with an Image() and re-requested
  // after 3 / 10 / 30 s (changed URL) if CloudFront answered 500 because the
  // driver's phone was still uploading it.
  const activePodRawUrl = podPhotos[activePhoto]?.url ?? podPhotos[0]?.url ?? null;
  const activeUrl = useProbedImageSrc(activePodRawUrl ?? undefined);
  // The list is one entry per PHOTO (a delivery with three POD photos appears
  // three times), so the banner counts distinct deliveries from the list itself.
  // `verifiedPastCount` (the PDR's Senior-verified tally) is still accepted for
  // wiring compatibility but no longer drives the banner: Agents and Seniors
  // see the same list (server/lib/verified-past.ts), so the count must come
  // from that list too.
  void verifiedPastCount;
  const verifiedCount = new Set(verifiedPast.map((p) => p.id)).size;
  const safeActivePast = activePast < verifiedPast.length ? activePast : 0;
  const activePastEntry = verifiedPast[safeActivePast] ?? verifiedPast[0] ?? null;
  return (
    <div
      data-testid="agent-detail-photos-card"
      style={{
        background: TOKENS.card,
        border: `1px solid ${TOKENS.border}`,
        borderRadius: 8,
        padding: 12,
        boxShadow: "0 1px 2px rgba(16,24,40,.06)",
        fontFamily: Starling.fontBody,
      }}
    >
      <div
        style={{
          display: "flex",
          gap: 4,
          marginBottom: 10,
          borderBottom: `1px solid ${TOKENS.border}`,
        }}
      >
        <button
          type="button"
          data-testid="agent-detail-photo-tab-pod"
          onClick={() => setTab("pod")}
          style={tabStyle(tab === "pod")}
        >
          POD <CountBadge n={podPhotos.length} active={tab === "pod"} />
        </button>
        <button
          type="button"
          data-testid="agent-detail-photo-tab-past"
          onClick={() => setTab("past")}
          style={tabStyle(tab === "past")}
        >
          Verified past <CountBadge n={verifiedPast.length} active={tab === "past"} />
        </button>
      </div>
      {tab === "pod" ? (
        <>
          <div
            style={{
              aspectRatio: "4/3",
              borderRadius: 6,
              background: activeUrl ? "#000" : "linear-gradient(160deg, #4B5563, #1F2937)",
              position: "relative",
              overflow: "hidden",
            }}
            data-testid="agent-detail-pod-main"
          >
            {/* A real <img>, not a CSS background, so the browser's own right-click
                "Open image in new tab" works — the same as a Senior's POD photo in
                qa-detail-v3 (Sarah 2026-10-06). Left click opens it too, as there. */}
            {activeUrl && (
              <img
                src={activeUrl}
                alt={`Proof of delivery photo ${activePhoto + 1}`}
                data-testid="agent-detail-pod-main-img"
                style={{ width: "100%", height: "100%", objectFit: "contain", display: "block", cursor: "zoom-in" }}
                title="Open full size in a new tab"
                onClick={() => openPhotoInNewTab(activePodRawUrl)}
              />
            )}
            <div
              style={{
                position: "absolute",
                bottom: 12,
                left: 12,
                background: "rgba(0,0,0,.45)",
                color: "#fff",
                fontSize: 11,
                padding: "3px 8px",
                borderRadius: 5,
              }}
            >
              Photo {activePhoto + 1} of {podPhotos.length} · proof of delivery
            </div>
          </div>
          <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
            {podPhotos.map((p, i) => (
              <PodThumb
                key={p.id ?? i}
                data-testid="agent-detail-pod-thumb"
                url={p.url}
                active={i === activePhoto}
                onClick={() => setActivePhoto(i)}
              />
            ))}
          </div>
          {verifiedCount > 0 && (
            <div
              data-testid="agent-detail-verified-banner"
              style={{
                marginTop: 10,
                padding: "8px 10px",
                background: TOKENS.goodCalmBg,
                color: TOKENS.goodCalm,
                border: "1px solid rgba(22,101,52,.18)",
                borderRadius: 6,
                fontSize: 12,
                display: "flex",
                alignItems: "center",
                gap: 7,
              }}
            >
              <span>✓</span>
              <span>
                <strong>{verifiedCount} verified past deliveries</strong> to this recipient.
              </span>
              <span
                style={{
                  color: TOKENS.text,
                  opacity: 0.7,
                  marginLeft: "auto",
                  fontSize: 11,
                  fontWeight: 500,
                }}
              >
                Tap Verified past to compare
              </span>
            </div>
          )}
        </>
      ) : (
        <div data-testid="agent-detail-verified-past">
          {verifiedPast.length === 0 ? (
            <div style={{ fontSize: 12, color: TOKENS.txt2 }}>
              No verified past deliveries yet.
            </div>
          ) : (
            <>
              {/* SPEC-AgentPhotos-VerifiedPast-Fix — mirror the POD layout:
                  main image + caption + thumbnail strip. Caption format is
                  "<date> · <dropZone or '—'> · verified by <verifiedBy or
                  'agent'>". Date uses toLocaleDateString to match POD's
                  free-form formatting; dropZone and verifiedBy fall back
                  per the SPEC's null-handling. */}
              <div
                style={{
                  aspectRatio: "4/3",
                  borderRadius: 6,
                  background: activePastEntry?.photoUrl ? "#000" : "linear-gradient(160deg, #4B5563, #1F2937)",
                  position: "relative",
                  overflow: "hidden",
                }}
                data-testid="agent-detail-verified-past-main"
              >
                {activePastEntry?.photoUrl && (
                  <img
                    src={activePastEntry.photoUrl}
                    alt="Past verified delivery photo"
                    data-testid="agent-detail-verified-past-main-img"
                    style={{ width: "100%", height: "100%", objectFit: "contain", display: "block", cursor: "zoom-in" }}
                    title="Open full size in a new tab"
                    onClick={() => openPhotoInNewTab(activePastEntry.photoUrl)}
                  />
                )}
                <div
                  style={{
                    position: "absolute",
                    bottom: 12,
                    left: 12,
                    background: "rgba(0,0,0,.45)",
                    color: "#fff",
                    fontSize: 11,
                    padding: "3px 8px",
                    borderRadius: 5,
                  }}
                >
                  {pastCaption(activePastEntry)}
                </div>
              </div>
              <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
                {verifiedPast.map((p, i) => (
                  <button
                    key={`${p.id}-${i}`}
                    type="button"
                    data-testid="agent-detail-verified-past-thumb"
                    onClick={() => setActivePast(i)}
                    style={{
                      width: 54,
                      height: 44,
                      borderRadius: 5,
                      overflow: "hidden",
                      background: "linear-gradient(135deg, #6B7280, #374151)",
                      cursor: "pointer",
                      opacity: i === safeActivePast ? 1 : 0.65,
                      border: i === safeActivePast
                        ? `2px solid ${TOKENS.purple}`
                        : "2px solid transparent",
                      padding: 0,
                    }}
                  >
                    {/* A real <img loading="lazy"> rather than a CSS background: the
                        shared list is one entry per photo and can run to hundreds, and
                        background images all load at once while lazy images wait until
                        they scroll into view. */}
                    {p.photoUrl && (
                      <img
                        src={p.photoUrl}
                        alt=""
                        loading="lazy"
                        style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                      />
                    )}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// SPEC-AgentPhotos-VerifiedPast-Fix §"Render layout" Decision #8 — caption
// format `<date> · <dropZone or '—'> · verified by <verifiedBy or 'agent'>`.
// `toLocaleDateString` matches POD's free-form date formatting; null fields
// degrade gracefully without re-rendering an empty pill.
function pastCaption(entry: PhotosPanelVerifiedPast | null): string {
  if (!entry) return "—";
  const date = entry.completedAt
    ? new Date(entry.completedAt).toLocaleDateString()
    : "—";
  const dz = entry.dropZone ?? "—";
  const verifier = entry.verifiedBy ?? "agent";
  return `${date} · ${dz} · verified by ${verifier}`;
}

/**
 * Open the photo file itself in a new browser tab (Sarah 2026-10-06). This is
 * the same thing Seniors get by clicking a POD photo in qa-detail-v3. It is an
 * external image URL, not a second QAM delivery, so the Agent single-slot rule
 * (SPEC-ActivePointer: one open delivery per Agent) is not involved.
 */
export function openPhotoInNewTab(url: string | null | undefined): void {
  if (!url) return;
  window.open(url, "_blank", "noopener,noreferrer");
}

/**
 * A2: one thumbnail. Its own component so each thumbnail can probe + retry
 * its URL independently (see useProbedImageSrc).
 */
function PodThumb({ url, active, onClick, "data-testid": testId }: { url: string | null | undefined; active: boolean; onClick: () => void; "data-testid": string }) {
  const src = useProbedImageSrc(url);
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      style={{
        width: 54,
        height: 44,
        borderRadius: 5,
        background: src
          ? `#000 url(${src}) center/cover no-repeat`
          : "linear-gradient(135deg, #6B7280, #374151)",
        cursor: "pointer",
        opacity: active ? 1 : 0.65,
        border: active ? `2px solid ${TOKENS.purple}` : "2px solid transparent",
        padding: 0,
      }}
    />
  );
}

function CountBadge({ n, active }: { n: number; active: boolean }) {
  return (
    <span
      style={{
        background: active ? TOKENS.purpleSub : TOKENS.sub,
        color: active ? TOKENS.purple : TOKENS.txt2,
        padding: "1px 6px",
        borderRadius: 999,
        fontSize: 10,
        fontWeight: 700,
        marginLeft: 6,
      }}
    >
      {n}
    </span>
  );
}

function tabStyle(active: boolean): React.CSSProperties {
  return {
    padding: "6px 12px",
    fontFamily: Starling.fontHead,
    fontSize: 12,
    fontWeight: 600,
    color: active ? TOKENS.purple : TOKENS.txt2,
    cursor: "pointer",
    borderBottom: `2px solid ${active ? TOKENS.purple : "transparent"}`,
    marginBottom: -1,
    display: "inline-flex",
    alignItems: "center",
    background: "transparent",
    border: "none",
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
  };
}

export default PhotosPanel;
